import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { FieldsetToggleComponent } from "@components/fieldset-toggle/fieldset-toggle.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { chain, flatMap, uniq } from "lodash";
import { EventService } from "~app/common/services/cqs/event.service";
import { TplEventHandler, TplTransporttilldelningModified } from "../../../events/tplEventHandler";
import { IKeyValue, ITplArbetsorder } from "../../../models/dtos";
import { IBefraktare, ITransportforetag } from "../../../models/queries";
import { TplService } from "../../../services/tpl.service";
import { TplStateService } from "../../../state/tpl-state.service";
import { ITilldelningOption, TransporttilldelningOptions } from "../../tpl-transporttilldelning/models/tpl-transporttilldelning.model";
import { FilterBaseComponent } from "../filter-base-component";
import { TplFilterContext } from "../tpl-filter-context";
import { ITplArbetsorderFilter } from "../tpl-filter.models";

@Component({
    selector: "rask-tpl-transportforetag-filter",
    templateUrl: "./tpl-transportforetag-filter.component.html",
    styleUrls: ["./tpl-transportforetag-filter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FieldsetToggleComponent,
        FormsModule,
        ReactiveFormsModule,
        MultiSelectorComponent
    ]
})
export class TplTransportforetagFilterComponent extends FilterBaseComponent implements OnInit, OnDestroy, ITplArbetsorderFilter {
    @Input() dataSource: ITplArbetsorder[];
    options: TransporttilldelningOptions;

    private readonly eventHandler: TplEventHandler;
    private readonly eventService = inject(EventService);
    private readonly tplService = inject(TplService);
    private readonly tplStateService = inject(TplStateService);

    private selectedOptions: ITilldelningOption[];

    constructor() {
        super("transportforetag");

        this.eventHandler = new TplEventHandler(this.eventService);
    }

    isActive(): boolean {
        return this.value().length > 0;
    }

    value(): string[] {
        return this.control.value;
    }

    keyValueSearchFn = (val: string, item: IKeyValue) => item.value?.toUpperCase().includes(val?.toUpperCase()) ?? false;

    match(item: ITplArbetsorder): boolean {
        return this.selectedOptions.some(option => item.handelssortiment.some(hs => option.match(item.transportforetagId, hs)));
    }

    ngOnDestroy(): void {
        this.eventHandler.unsubscribe();
        super.ngOnDestroy();
    }

    ngOnInit(): void {
        this.eventHandler.onTransporttilldelningModified(e => this.onTransporttilldelningModified(e));

        this.initOptions();
        this.onFormInit(new UntypedFormControl([]));
    }

    onFilterInit(state: string[]): void {
        state = state ?? [];
        const validated = this.options.getMany(state).map(x => x.key);
        this.control.setValue(validated);
    }

    prepare() {
        this.selectedOptions = this.options.getMany(this.value());
    }

    protected onAddFilterToContext(filterContext: TplFilterContext) {
        filterContext.addArbetsorderFilter(this.name, this);
    }

    protected onFormChange(value: number[]): void {
        this.filterLength = value.length;
    }

    private getApplicableBefraktare(): IBefraktare[] {
        const applicableIdList = chain(this.dataSource)
            .flatMap(x => x.handelssortiment)
            .filter(x => x.befraktareId > 0)
            .map(x => x.befraktareId)
            .uniq()
            .value();

        return this.tplService.allBefraktare()
            .filter(x => applicableIdList.includes(x.id));
    }

    private getApplicableTransportforetag(): ITransportforetag[] {
        const level1 = this.dataSource.map(x => x.transportforetagId);
        const level2 = flatMap(this.dataSource, x => x.handelssortiment).filter(x => x.transportforetagId > 0).map(x => x.transportforetagId);
        const applicableTransportforetagIdList = uniq([
            ...level1,
            ...level2
        ]);

        return this.tplStateService.current
            .getTransportforetag()
            .filter(x => applicableTransportforetagIdList.includes(x.id));
    }

    private initOptions() {
        const transportforetag = this.getApplicableTransportforetag();
        const befraktare = this.getApplicableBefraktare();

        this.options = new TransporttilldelningOptions(befraktare, transportforetag);
    }

    private onTransporttilldelningModified(event: TplTransporttilldelningModified): void {
        this.initOptions();
        this.onFilterInit(this.value());
    }
}
